import { OrderBumpsProvider } from '@/components/config-editor/providers/order-bumps-provider';
import { UpsellsProvider } from '@/components/config-editor/providers/upsells-provider';
import {
  OfferSelector,
  OrderBumpSelector,
  OrderSelector,
  PriceSelector,
  ProductSelector,
  VariantSelector,
} from '@/components/resource-pickers';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LocaleSelector } from '@/components/locale-selector';
import { trpc } from '@/lib/api/trpc';
import {
  detectResourceRequirements,
  type ResourceRequirement,
} from '@/lib/plugin-routing/resource-detection';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertCircle, ChevronDown, CreditCard, ExternalLink, Eye, Gift, Globe, Package, Settings2, ShoppingCart, Shuffle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { CheckoutSessionBuilder } from './CheckoutSessionBuilder';

/**
 * Test card presets for preview sessions (must match backend)
 */
const TEST_CARD_PRESETS = {
  visa_success: {
    label: 'Visa 4242 (Success)',
    description: 'Standard successful payment',
    cardNumber: '4242 4242 4242 4242',
    brand: 'visa',
  },
  mastercard_success: {
    label: 'Mastercard 4444 (Success)',
    description: 'Mastercard successful payment',
    cardNumber: '5555 5555 5555 4444',
    brand: 'mastercard',
  },
  amex_success: {
    label: 'Amex 0005 (Success)',
    description: 'American Express successful payment',
    cardNumber: '3782 8224 6310 005',
    brand: 'amex',
  },
  visa_declined: {
    label: 'Visa 0002 (Declined)',
    description: 'Card will be declined',
    cardNumber: '4000 0000 0000 0002',
    brand: 'visa',
  },
  visa_insufficient: {
    label: 'Visa 9995 (Insufficient)',
    description: 'Insufficient funds error',
    cardNumber: '4000 0000 0000 9995',
    brand: 'visa',
  },
  visa_3ds: {
    label: 'Visa 0341 (3DS Required)',
    description: 'Will trigger 3D Secure flow',
    cardNumber: '4000 0000 0000 0341',
    brand: 'visa',
  },
} as const;


interface StepVariant {
  id: string;
  label: string;
  percentage: number;
  config: {
    url?: string;
    instanceId?: string;
    variant?: string;
    metadata?: {
      [key: string]: any;
      routeType?: 'mount-point' | 'external' | 'instance';
      internalPath?: string;
    };
  };
}

interface PreviewRequirementsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: string;
  funnelId: string;
  stepId: string;
  instanceId: string;
  stepUrl: string;
  internalPath?: string;
  variants?: StepVariant[];
  environment: 'staging' | 'production';
  variantId?: string;
  onScreenshotTriggered?: () => void;
}

function getResourceIcon(resourceType: string) {
  const icons: Record<string, any> = {
    product: Package,
    variant: Package,
    price: Package,
    offer: Gift,
    orderBump: Gift,
    order: ShoppingCart,
    checkoutSession: ShoppingCart,
  };
  return icons[resourceType] || Package;
}

export function PreviewRequirementsDialog({
  isOpen,
  onClose,
  storeId,
  funnelId,
  stepId,
  instanceId,
  stepUrl,
  internalPath,
  variants,
  environment,
  variantId,
  onScreenshotTriggered,
}: PreviewRequirementsDialogProps) {
  const { t } = useTranslation('storefront');
  const [resourceValues, setResourceValues] = useState<Record<string, string>>({});
  const [manualParams, setManualParams] = useState<Record<string, string>>({});
  const [checkoutLineItems, setCheckoutLineItems] = useState<
    Array<{ variantId: string; priceId: string; quantity: number }>
  >([]);
  const [variantMode, setVariantMode] = useState<'random' | 'specific'>('random');
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [enableSticky, setEnableSticky] = useState(true);

  // Display settings: currency and locale
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');
  const [selectedLocale, setSelectedLocale] = useState<string>('auto');

  // Test card for payment-required previews
  const [testCardPreset, setTestCardPreset] = useState<keyof typeof TEST_CARD_PRESETS | null>(null);

  // Advanced section collapsed by default
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // ✅ UI Cooldown: Prevent spam clicking (10 seconds)
  const [lastPreviewTime, setLastPreviewTime] = useState<number>(0);
  const PREVIEW_COOLDOWN = 10000; // 10 seconds

  const { data: instance, isLoading } = trpc.plugins.getInstanceMetadata.useQuery(
    { instanceId },
    { enabled: isOpen && !!instanceId, staleTime: 60 * 1000 },
  );

  // Fetch store data for currency options
  const { data: store } = trpc.stores.getById.useQuery(
    { storeId },
    { enabled: isOpen && !!storeId },
  );

  // Set default currency when store loads
  useEffect(() => {
    if (store?.baseCurrency && !selectedCurrency) {
      setSelectedCurrency(store.baseCurrency);
    }
  }, [store?.baseCurrency, selectedCurrency]);

  const utils = trpc.useUtils();

  const captureScreenshotMutation = trpc.funnels.captureStepScreenshot.useMutation();

  const createPreviewMutation = trpc.funnels.createPreviewSession.useMutation({
    onSuccess: (data) => {
      window.open(data.previewUrl, '_blank', 'noopener,noreferrer');

      // Trigger screenshot capture in background
      // Note: Screenshots are stored in node.metadata and shared between staging/production
      // The URL used for capture doesn't matter - both modes read from the same metadata
      // Always use data.previewUrl as it contains the fully resolved URL with all parameters filled in
      const screenshotUrl = data.previewUrl;

      // Start polling for screenshot BEFORE closing dialog
      onScreenshotTriggered?.();

      captureScreenshotMutation.mutate({
        funnelId,
        stepId,
        variantId,
        previewUrl: screenshotUrl,
        storeId,
      });

      onClose();
    },
    onError: (error) => {
      toast.error(t('previewRequirements.previewFailed', { error: error.message }));
    },
  });

  const pages = instance?.deployment?.pages;

  // Parse requirements from manifest using shared utility
  // Filter out static resources - they are now handled via stepConfig injection
  const { resourceRequirements: allResourceRequirements, manualRequirements, pathParamMap } = React.useMemo(() => {
    const urlPath = internalPath
      ? internalPath.replace(/^\//, '')
      : stepUrl.split('?')[0].split('/').pop() || '';

    return detectResourceRequirements(pages, urlPath, internalPath);
  }, [pages, stepUrl, internalPath]);

  // Only show non-static resources - static resources are configured in the step editor
  const resourceRequirements = React.useMemo(() => {
    return allResourceRequirements.filter((req) => !req.isStatic);
  }, [allResourceRequirements]);

  const missingRequired = React.useMemo(() => {
    const missing: string[] = [];

    resourceRequirements.forEach((req) => {
      // For checkoutSession, we don't require a pre-existing token - the preview system
      // creates a new checkout session from lineItems. Having lineItems OR empty cart is valid.
      if (req.resourceType === 'checkoutSession') {
        // Checkout sessions are always "satisfied" - empty cart previews are allowed
        return;
      }

      if (req.required && !resourceValues[req.resourceName]) {
        missing.push(req.label || req.resourceName);
      }
    });

    manualRequirements.forEach((req: any) => {
      if (req.required && !manualParams[req.name] && req.type !== 'path') {
        missing.push(req.name);
      }
    });

    return missing;
  }, [resourceRequirements, manualRequirements, resourceValues, manualParams]);

  const handleResourceChange = (req: ResourceRequirement, value: string) => {
    // Convert to string if needed (handles number IDs)
    const stringValue = typeof value === 'string' ? value : String(value);

    if (!stringValue || stringValue === '[object Object]' || stringValue === 'undefined' || stringValue === 'null') {
      console.error('[Preview] Invalid resource value:', { req, value, type: typeof value });
      toast.error(t('previewRequirements.invalidSelection', { resource: req.label || req.resourceName }));
      return;
    }

    setResourceValues((prev) => ({ ...prev, [req.resourceName]: stringValue }));
  };

  const handlePreview = () => {
    // ✅ Cooldown check: Prevent spam clicking
    const now = Date.now();
    const timeSinceLastPreview = now - lastPreviewTime;

    if (timeSinceLastPreview < PREVIEW_COOLDOWN) {
      const remainingSeconds = Math.ceil((PREVIEW_COOLDOWN - timeSinceLastPreview) / 1000);
      toast.error(t('previewRequirements.cooldownWait', { seconds: remainingSeconds }));
      return;
    }

    if (missingRequired.length > 0) {
      toast.error(t('previewRequirements.pleaseConfigure', { fields: missingRequired.join(', ') }));
      return;
    }

    if (variants && variants.length > 0 && variantMode === 'specific' && !selectedVariantId) {
      toast.error(t('previewRequirements.selectVariantError'));
      return;
    }

    // Update last preview time
    setLastPreviewTime(now);

    const builderOptions: Record<string, any> = {};

    resourceRequirements.forEach((req) => {
      // Include all non-static resources (including path params)
      // Path params need to be included so they can replace :paramName in the URL
      if (!req.isStatic && resourceValues[req.resourceName]) {
        // Use paramName which is the actual parameter name for URL building
        const paramName = req.paramName || req.name || req.resourceName;
        builderOptions[paramName] = resourceValues[req.resourceName];
      }
    });

    // Add checkout session line items if checkoutSession resource exists
    const checkoutSessionReq = resourceRequirements.find((req) => req.resourceType === 'checkoutSession');
    if (checkoutSessionReq && checkoutLineItems.length > 0) {
      builderOptions.lineItems = checkoutLineItems;
    }

    Object.entries(manualParams).forEach(([key, value]) => {
      if (value) {
        builderOptions[key] = value;
      }
    });

    // Check if all order resources are in preview mode (no real orders selected)
    const orderReqs = resourceRequirements.filter((req) => req.resourceType === 'order');
    const allOrdersPreview = orderReqs.length > 0 && orderReqs.every((req) => resourceValues[req.resourceName] === 'preview');

    if (allOrdersPreview && !resourceRequirements.some((req) => req.resourceType !== 'order')) {
      // Direct redirect: build URL without creating a preview session
      let previewUrl = stepUrl;

      // Replace path params (e.g., :orderId -> preview)
      Object.entries(builderOptions).forEach(([key, value]) => {
        if (previewUrl.includes(`:${key}`)) {
          previewUrl = previewUrl.replace(`:${key}`, value);
        }
      });

      const url = new URL(previewUrl);
      url.searchParams.set('funnelTracking', 'false');
      if (selectedCurrency) url.searchParams.set('currency', selectedCurrency);
      if (selectedLocale && selectedLocale !== 'auto') url.searchParams.set('locale', selectedLocale);

      window.open(url.toString(), '_blank', 'noopener,noreferrer');
      onClose();
      return;
    }

    createPreviewMutation.mutate({
      storeId,
      funnelId,
      stepId,
      builderOptions,
      environment,
      funnelVariantId: variantMode === 'specific' && selectedVariantId ? selectedVariantId : undefined,
      enableSticky: variantMode === 'random' ? enableSticky : undefined,
      currency: selectedCurrency || undefined,
      locale: selectedLocale && selectedLocale !== 'auto' ? selectedLocale : undefined,
      testCardPreset: testCardPreset || undefined,
    });
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t('previewRequirements.loading')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground text-sm">{t('previewRequirements.loadingRequirements')}</div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const hasRequirements = resourceRequirements.length > 0 || manualRequirements.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            {t('previewRequirements.title')}
          </DialogTitle>
          <DialogDescription>
            {variants && variants.length > 0
              ? t('previewRequirements.descriptionWithVariants')
              : t('previewRequirements.descriptionNoVariants')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">

          {variants && variants.length > 0 && (
            <div className="space-y-3 border-b pb-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-purple-900 dark:text-purple-100">
                <Shuffle className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                <span>{t('previewRequirements.abTestPreviewMode')}</span>
              </div>

              <RadioGroup
                value={variantMode}
                onValueChange={(val) => setVariantMode(val as 'random' | 'specific')}
                className="grid grid-cols-2 gap-3"
              >
                <div
                  className={`hover:bg-accent flex cursor-pointer flex-col space-y-1 rounded-md border p-3 transition-colors ${variantMode === 'random'
                      ? 'border-purple-500 bg-purple-50/50 dark:bg-purple-900/20'
                      : 'border-border'
                    }`}
                  onClick={() => setVariantMode('random')}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="random" id="random" />
                    <Label htmlFor="random" className="cursor-pointer text-sm font-medium">
                      {t('previewRequirements.randomSplit')}
                    </Label>
                  </div>
                  <p className="text-muted-foreground pl-6 text-[10px] leading-tight">{t('previewRequirements.randomSplitDesc')}</p>
                </div>

                <div
                  className={`hover:bg-accent flex cursor-pointer flex-col space-y-1 rounded-md border p-3 transition-colors ${variantMode === 'specific'
                      ? 'border-purple-500 bg-purple-50/50 dark:bg-purple-900/20'
                      : 'border-border'
                    }`}
                  onClick={() => setVariantMode('specific')}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="specific" id="specific" />
                    <Label htmlFor="specific" className="cursor-pointer text-sm font-medium">
                      {t('previewRequirements.specificVariant')}
                    </Label>
                  </div>
                  <p className="text-muted-foreground pl-6 text-[10px] leading-tight">{t('previewRequirements.specificVariantDesc')}</p>
                </div>
              </RadioGroup>

              {variantMode === 'random' && (
                <div className="flex items-center space-x-2 px-1 pt-1">
                  <Switch
                    id="sticky"
                    checked={enableSticky}
                    onCheckedChange={setEnableSticky}
                    className="origin-left scale-75"
                  />
                  <Label htmlFor="sticky" className="text-muted-foreground cursor-pointer text-xs font-medium">
                    {t('previewRequirements.stickySession')}
                  </Label>
                </div>
              )}

              {variantMode === 'specific' && (
                <div className="px-1 pt-1">
                  <Select value={selectedVariantId || ''} onValueChange={setSelectedVariantId}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder={t('previewRequirements.selectVariant')} />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      {variants?.map((variant) => (
                        <SelectItem key={variant.id} value={variant.id} className="text-xs">
                          <span className="font-medium">{variant.label}</span>{' '}
                          <span className="text-muted-foreground">({variant.percentage}%)</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* Display Settings: Currency and Locale */}
          <div className="space-y-3 border-b pb-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-primary">
              <Globe className="h-4 w-4 text-primary" />
              <span>{t('previewRequirements.displaySettings')}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{t('previewRequirements.currency')}</Label>
                <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder={t('previewRequirements.selectCurrency')} />
                  </SelectTrigger>
                  <SelectContent>
                    {store?.presentmentCurrencies?.map((code: string) => (
                      <SelectItem key={code} value={code} className="text-xs">
                        {code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-[10px]">
                  {t('previewRequirements.displayPricesInCurrency')}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{t('previewRequirements.language')}</Label>
                <LocaleSelector
                  value={selectedLocale}
                  onChange={setSelectedLocale}
                  showAuto
                  size="sm"
                />
                <p className="text-muted-foreground text-[10px]">
                  {t('previewRequirements.displayContentInLanguage')}
                </p>
              </div>
            </div>
          </div>

          {/* Advanced Settings (collapsed by default) */}
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="border-b pb-4">
            <CollapsibleTrigger className="flex w-full items-center justify-between py-2 text-sm font-medium hover:underline">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Settings2 className="h-4 w-4" />
                <span>{t('previewRequirements.advanced')}</span>
                {testCardPreset && (
                  <span className="rounded bg-emerald-100 dark:bg-emerald-900/30 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-300">
                    {t('previewRequirements.testCardSelected')}
                  </span>
                )}
              </div>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${advancedOpen ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3 space-y-3">
              {/* Test Payment Card */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                  <CreditCard className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <span>{t('previewRequirements.testPaymentCard')}</span>
                  <span className="text-muted-foreground text-[10px] font-normal">{t('previewRequirements.optional')}</span>
                </div>
                <p className="text-muted-foreground text-xs">
                  {t('previewRequirements.testCardDesc')}
                </p>
                <Select value={testCardPreset || 'none'} onValueChange={(v) => setTestCardPreset(v === 'none' ? null : v as keyof typeof TEST_CARD_PRESETS)}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder={t('previewRequirements.noTestCard')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" className="text-xs">
                      <span className="text-muted-foreground">{t('previewRequirements.noTestCard')}</span>
                    </SelectItem>
                    <div className="py-1 px-2 text-[10px] font-semibold text-muted-foreground uppercase">{t('previewRequirements.successCards')}</div>
                    {Object.entries(TEST_CARD_PRESETS)
                      .filter(([key]) => key.includes('success'))
                      .map(([key, card]) => (
                        <SelectItem key={key} value={key} className="text-xs">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium">{card.label}</span>
                            <span className="text-muted-foreground text-[10px]">{card.cardNumber}</span>
                          </div>
                        </SelectItem>
                      ))}
                    <div className="py-1 px-2 text-[10px] font-semibold text-muted-foreground uppercase">{t('previewRequirements.testScenarios')}</div>
                    {Object.entries(TEST_CARD_PRESETS)
                      .filter(([key]) => !key.includes('success'))
                      .map(([key, card]) => (
                        <SelectItem key={key} value={key} className="text-xs">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium">{card.label}</span>
                            <span className="text-muted-foreground text-[10px]">{card.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {testCardPreset && TEST_CARD_PRESETS[testCardPreset as keyof typeof TEST_CARD_PRESETS] && (
                  <div className="flex items-center gap-2 rounded-md bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 text-xs">
                    <CreditCard className="h-4 w-4 text-emerald-600" />
                    <div>
                      <span className="font-medium">{TEST_CARD_PRESETS[testCardPreset as keyof typeof TEST_CARD_PRESETS].cardNumber}</span>
                      <span className="text-muted-foreground ml-2">Exp: 12/28 • CVV: 123</span>
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {!hasRequirements ? (
            <div className="py-4">
              <Card className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-muted-foreground h-5 w-5" />
                  <div>
                    <p className="text-sm font-medium">{t('previewRequirements.noRequirements')}</p>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {t('previewRequirements.noRequirementsDesc')}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          ) : (
            <Tabs defaultValue={resourceRequirements[0]?.resourceName || 'manual'} className="w-full">
              <TabsList
                className="grid h-auto w-full truncate"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(3, resourceRequirements.length + (manualRequirements.length > 0 ? 1 : 0))}, minmax(0, 1fr))`,
                }}
              >
                {resourceRequirements.map((req) => {
                  const Icon = getResourceIcon(req.resourceType);
                  const hasValue = !!resourceValues[req.resourceName];

                  return (
                    <TabsTrigger
                      key={req.resourceName}
                      value={req.resourceName}
                      className="relative min-w-0 overflow-hidden"
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0 truncate">{req.label}</span>
                      {req.required && <span className="shrink-0 text-red-600">*</span>}
                    </TabsTrigger>
                  );
                })}
                {manualRequirements.length > 0 && (
                  <TabsTrigger value="manual" className="min-w-0 overflow-hidden">
                    <span className="truncate">{t('previewRequirements.manualParams')}</span>
                  </TabsTrigger>
                )}
              </TabsList>

              {resourceRequirements.map((req) => (
                <TabsContent key={req.resourceName} value={req.resourceName} className="space-y-3 pt-4">
                  <div>
                    <Label className="text-sm font-medium">
                      {req.label}
                      {req.required && <span className="ml-1 text-red-600">*</span>}
                    </Label>
                    {req.required && !resourceValues[req.resourceName] && (
                      <p className="text-muted-foreground mt-1 text-xs">
                        {t('previewRequirements.resourceRequired')}
                      </p>
                    )}
                  </div>

                  {req.resourceType === 'product' && (
                    <ProductSelector
                      value={resourceValues[req.resourceName] || ''}
                      onChange={(value) => handleResourceChange(req, value)}
                      storeId={storeId}
                      searchable={true}
                      placeholder={t('previewRequirements.searchSelectProduct')}
                    />
                  )}

                  {req.resourceType === 'variant' && (
                    <VariantSelector
                      value={resourceValues[req.resourceName] || ''}
                      onChange={(value) => handleResourceChange(req, value)}
                      storeId={storeId}
                    />
                  )}

                  {req.resourceType === 'price' && (
                    <PriceSelector
                      value={resourceValues[req.resourceName] || ''}
                      onChange={(value) => handleResourceChange(req, value)}
                      storeId={storeId}
                    />
                  )}

                  {req.resourceType === 'offer' && (
                    <OfferSelector
                      value={resourceValues[req.resourceName] || ''}
                      onChange={(value) => handleResourceChange(req, value)}
                      storeId={storeId}
                    />
                  )}

                  {req.resourceType === 'orderBump' && (
                    <UpsellsProvider storeId={storeId}>
                      <OrderBumpsProvider>
                        <OrderBumpSelector
                          value={resourceValues[req.resourceName] || ''}
                          onChange={(value) => handleResourceChange(req, value)}
                          storeId={storeId}
                        />
                      </OrderBumpsProvider>
                    </UpsellsProvider>
                  )}

                  {req.resourceType === 'order' && (
                    <OrderSelector
                      value={resourceValues[req.resourceName] || ''}
                      onChange={(value) => handleResourceChange(req, value)}
                      storeId={storeId}
                      searchable={true}
                      placeholder={t('previewRequirements.searchSelectOrder')}
                      allowPreviewMode={true}
                    />
                  )}

                  {req.resourceType === 'checkoutSession' && (
                    <CheckoutSessionBuilder
                      storeId={storeId}
                      checkoutUrl={stepUrl}
                      funnelId={funnelId}
                      stepId={stepId}
                      environment={environment}
                      funnelVariantId={
                        variantMode === 'specific' && selectedVariantId ? selectedVariantId : undefined
                      }
                      enableSticky={variantMode === 'random' ? enableSticky : undefined}
                      onLineItemsChange={setCheckoutLineItems}
                    />
                  )}
                </TabsContent>
              ))}

              {manualRequirements.length > 0 && (
                <TabsContent value="manual" className="space-y-3 pt-4">
                  {manualRequirements.map((req: any) => (
                    <div key={req.name} className="space-y-2">
                      <Label className="text-sm font-medium">
                        {req.name}
                        {req.required && <span className="ml-1 text-red-600">*</span>}
                        <span className="text-muted-foreground ml-2 text-xs">({req.type})</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder={t('previewRequirements.enterParam', { name: req.name })}
                        value={manualParams[req.name] || ''}
                        onChange={(e) => setManualParams({ ...manualParams, [req.name]: e.target.value })}
                        className="font-mono text-sm"
                      />
                    </div>
                  ))}
                </TabsContent>
              )}
            </Tabs>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            {t('previewRequirements.cancel')}
          </Button>
          <Button
            onClick={handlePreview}
            disabled={
              createPreviewMutation.isPending ||
              missingRequired.length > 0 ||
              Date.now() - lastPreviewTime < PREVIEW_COOLDOWN
            }
          >
            {createPreviewMutation.isPending ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {t('previewRequirements.creating')}
              </>
            ) : (
              <>
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('previewRequirements.openPreview')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
